import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Countdown } from '../components/Countdown';
import { DiscoBall, HeartArrow } from '../components/DecorativeElements';
import { coupleData } from '../mockData';
import { ArrowRight, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { downloadCalendar } from '../utils/calendar';

export const Home = () => {
  const { t, language } = useLanguage();
  
  return (
    <div className="min-h-screen cream-bg">
      <Navbar />
      
      {/* Main Content - Centered on large screens */}
      <section className="pt-32 pb-4 px-6 sm:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          
          {/* Hero Section with Disco Ball and Invitation */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 mb-8 mt-8 items-center">
            {/* Left - Disco Ball */}
            <div className="flex items-center justify-center lg:justify-end">
              <DiscoBall className="w-[280px] h-[280px] lg:w-[333px] lg:h-[333px]" />
            </div>
            
            {/* Right - Invitation Text */}
            <div className="flex items-center justify-center lg:justify-start">
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold blue-text leading-tight whitespace-pre-line text-center lg:text-left">
                {t('home.invitation')}
              </h2>
            </div>
          </div>

          {/* Countdown Timer and Couple Photo - Same Level */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 mb-16 items-center">
            {/* Left - Countdown Timer */}
            <div className="flex justify-center lg:justify-end">
              <div className="border-2 border-primary cream-bg w-full max-w-md p-8">
                <h3 className="font-sans text-xs tracking-widest uppercase text-center blue-text mb-6">
                  {t('home.countdown')}
                </h3>
                
                {/* Countdown Timer */}
                <div className="mb-6">
                  <Countdown 
                    targetDate={coupleData.weddingDate}
                    daysLabel={t('home.days')}
                    hoursLabel={t('home.hours')}
                    minutesLabel={t('home.minutes')}
                    secondsLabel={t('home.seconds')}
                  />
                </div>
              </div>
            </div>

            {/* Right - Couple Photo */}
            <div className="flex justify-center lg:justify-start">
              <div className="w-full max-w-md aspect-square cream-bg flex items-center justify-center rounded-sm overflow-hidden border-2 border-primary">
                <img 
                  src="/couple-photo-new.jpg" 
                  alt="Polina and Evgeniy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons - RSVP, Details, Travel, Gallery */}
          <div className="max-w-3xl mx-auto mt-16 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/rsvp">
                <button className="w-full border-2 border-primary blue-bg text-white py-4 font-sans text-xs tracking-widest uppercase hover:opacity-90 transition-opacity">
                  {t('nav.rsvp')}
                </button>
              </Link>
              
              <Link to="/schedule">
                <button className="w-full border-2 border-primary blue-bg text-white py-4 font-sans text-xs tracking-widest uppercase hover:opacity-90 transition-opacity">
                  {t('nav.details')}
                </button>
              </Link>
              
              <Link to="/travel">
                <button className="w-full border-2 border-primary blue-bg text-white py-4 font-sans text-xs tracking-widest uppercase hover:opacity-90 transition-opacity">
                  {t('nav.travel')}
                </button>
              </Link>
              
              <Link to="/gallery">
                <button className="w-full border-2 border-primary blue-bg text-white py-4 font-sans text-xs tracking-widest uppercase hover:opacity-90 transition-opacity">
                  {t('nav.gallery')}
                </button>
              </Link>
            </div>
          </div>

          {/* Add to Calendar Button */}
          <div className="max-w-3xl mx-auto mb-8">
            <button
              onClick={downloadCalendar}
              className="w-full border-2 border-primary cream-bg blue-text py-4 font-sans text-xs tracking-widest uppercase hover:blue-bg hover:text-white transition-all duration-300 flex items-center justify-center gap-3"
            >
              <Calendar className="w-5 h-5" />
              <span>{t('home.addToCalendar')}</span>
            </button>
          </div>

          {/* WhatsApp Group Section */}
          <div className="max-w-3xl mx-auto mb-16">
            <a 
              href="https://chat.whatsapp.com/BDubnkNSNxqCSlnsQ58FqN"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="border-2 border-primary cream-bg p-6 hover:blue-bg hover:text-white transition-all duration-300 group">
                <div className="flex items-center justify-center gap-4">
                  {/* WhatsApp Icon */}
                  <svg 
                    className="w-8 h-8 flex-shrink-0 blue-text group-hover:text-white transition-colors" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  
                  {/* Text */}
                  <div className="text-center">
                    <p className="font-sans text-sm md:text-base font-semibold blue-text group-hover:text-white transition-colors">
                      {t('whatsapp.joinGroup')}
                    </p>
                  </div>
                  
                  {/* Arrow */}
                  <svg 
                    className="w-5 h-5 flex-shrink-0 blue-text group-hover:text-white transition-all group-hover:translate-x-1" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </a>

            {/* Telegram Group - Only for Russian */}
            {language === 'ru' && (
              <a 
                href="https://web.telegram.org/k/#-5173125195"
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-4"
              >
                <div className="border-2 border-primary cream-bg p-6 hover:blue-bg hover:text-white transition-all duration-300 group">
                  <div className="flex items-center justify-center gap-4">
                    {/* Telegram Icon */}
                    <svg 
                      className="w-8 h-8 flex-shrink-0 blue-text group-hover:text-white transition-colors" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                    >
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.121.099.154.232.17.326.016.094.036.308.02.476z"/>
                    </svg>
                    
                    {/* Text */}
                    <div className="text-center">
                      <p className="font-sans text-sm md:text-base font-semibold blue-text group-hover:text-white transition-colors">
                        {t('telegram.joinGroup')}
                      </p>
                    </div>
                    
                    {/* Arrow */}
                    <svg 
                      className="w-5 h-5 flex-shrink-0 blue-text group-hover:text-white transition-all group-hover:translate-x-1" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </a>
            )}
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};